const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/components/AppBar/IconBtn.tsx';
import * as React from 'react';
import MuiIconBtn from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';
import { goBack } from 'redux-first-history';
import { expandMainNav } from 'App/actions/ui';
import textMap from './translations';
import { useIntl } from 'react-intl';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { Badge } from '@mui/material';
import { useDispatch } from 'react-redux';

import type { JSX } from "react";

type IconBtnKind = 'menu' | 'back' | 'cross' | 'none' | 'logout' | 'profile' | 'search' | 'notification';

/**
 * React props
 */
export interface IIconBtnProps {
    kind?: IconBtnKind; // 'none' by default
    backBtnClicked?: () => boolean; // return true to override the default implementation
    menuBtnClicked?: () => boolean; // return true to override the default implementation
    onClick?: () => void | boolean;
    marginTop?: number; // if this is set, the icon will align itself to flex-start
    marginRight?: number; // if this is set, the icon will right-align
    badgeText?: string;
    intl?: any;
}

/**
 * React props including injected props
 */

/**
 * React component
 * NOTE: This is *not* a functional component because of performance issues when used
 * inside an AppBar with variable height.
 */
export const IconBtn = (props: IIconBtnProps) => {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch<any>();

    const leftBtnClicked = () => {
        if (props.kind === 'back' || props.kind === 'cross') {
            if (!props.backBtnClicked || !props.backBtnClicked()) {
                dispatch(goBack());
            }
        } else if (props.onClick !== undefined) {
            props.onClick();
        } else {
            if (!props.menuBtnClicked || !props.menuBtnClicked()) {
                dispatch(expandMainNav());
            }
        }
    };

    const hasMarginTop = typeof props.marginTop === 'number';
    const inlineStyle: React.CSSProperties = {
        alignSelf: !hasMarginTop ? undefined : 'flex-start',
        marginTop: props.marginTop,
        marginRight: props.marginRight,
    };

    const getBtnIcon = (kind?: IconBtnKind): JSX.Element | null => {
        switch (kind) {
            case 'back':
                return <Icon className="icons8-arrow-left" />;
            case 'menu':
                return <Icon className="icons8-menu" />;
            case 'logout':
                return <Icon className="icons8-logout" />;
            case 'search':
                return <Icon className="icons8-search" />;
            case 'notification':
                return props.badgeText ? (
                    <Badge max={99} badgeContent={props.badgeText} color="error">
                        <NotificationsNoneOutlinedIcon />
                    </Badge>
                ) : (
                    <NotificationsNoneOutlinedIcon />
                );
            case 'profile':
                return <Icon className="icons8-user" />;
            case 'cross':
                return <Icon className="icons8-delete" />;
            case 'none':
            default:
                return null;
        }
    };

    const btnIcon = getBtnIcon(props.kind);

    return (
        btnIcon && (
            <MuiIconBtn
                color="inherit"
                onClick={leftBtnClicked}
                style={inlineStyle}
                aria-label={formatMessage(textMap.ButtonBack)}
            >
                {btnIcon}
            </MuiIconBtn>
        )
    );
};
